import Button from '@atlaskit/button';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import { useDatabaseStreamState } from '@databases/database-ai-first-creation-ui';
import { type Adjacents } from '@databases/redux/cellSelections/selectors';
import { useSkeletonEnabled } from '@databases/skeleton';
import {
  invisibleBorder,
  selectedBorder,
  TABLE_BORDER_RADIUS,
  visualRefreshSelectBorder,
} from '@databases/styles';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import classNames from 'classnames';
import React, {
  type CSSProperties,
  forwardRef,
  type ReactChild,
  type ReactNode,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';
import { useSnapshot } from 'valtio';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

export const InlineEditWrapperContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: ${token('radius.small', '3px')};
  overflow: visible;

  &.${TABLE_CLASSNAMES.IS_HOVER_DELETE_BUTTON}&.${TABLE_CLASSNAMES.IS_SELECTED}:not(.${TABLE_CLASSNAMES.HIDE_BORDER_ON_FOCUS}),
  &.${TABLE_CLASSNAMES.IS_HOVER_DELETE_BUTTON}&.${TABLE_CLASSNAMES.IS_SELECTED} {
    transition: background-color 0.2s;
    background-color: ${token('color.background.danger.hovered', colors.R50)};
  }

  &.${TABLE_CLASSNAMES.IS_ROVO_EDIT_PROCESSING} {
    opacity: 0.4 !important;
  }
`;

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
export const BorderContainer = styled.div<{
  isVisualRefreshM1Enabled: boolean;
  borderStyle: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: ${invisibleBorder};
  border-radius: ${TABLE_BORDER_RADIUS};
  margin: -1px -1px -1px 0;
  pointer-events: none;

  ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_INVALID}:not(.${TABLE_CLASSNAMES.HIDE_BORDER_ON_FOCUS}) > &,
  ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_INVALID}:not(:focus-within):not(.${TABLE_CLASSNAMES.IS_SELECTED}) > & {
    border-color: ${colors.R300};
    margin: 0;
  }
  ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_SELECTED} > & {
    border-style: none;
    border-radius: 0;
  }

  ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_SELECTED}:not(.${TABLE_CLASSNAMES.IS_TOP_ADJACENT_SELECTED}) > & {
    border-top: ${(props) => props.borderStyle};
  }

  ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_SELECTED}:not(.${TABLE_CLASSNAMES.IS_BOTTOM_ADJACENT_SELECTED}) > & {
    border-bottom: ${(props) => props.borderStyle};
  }

  ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_SELECTED}:not(.${TABLE_CLASSNAMES.IS_LEFT_ADJACENT_SELECTED}) > & {
    border-left: ${(props) => props.borderStyle};
  }

  ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_SELECTED}:not(.${TABLE_CLASSNAMES.IS_RIGHT_ADJACENT_SELECTED}) > & {
    border-right: ${(props) => props.borderStyle};
  }

  ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_SELECTED}:not(.${TABLE_CLASSNAMES.IS_TOP_ADJACENT_SELECTED}):not(.${TABLE_CLASSNAMES.IS_LEFT_ADJACENT_SELECTED}) > & {
    border-start-start-radius: ${TABLE_BORDER_RADIUS};
  }

  ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_SELECTED}:not(.${TABLE_CLASSNAMES.IS_TOP_ADJACENT_SELECTED}):not(.${TABLE_CLASSNAMES.IS_RIGHT_ADJACENT_SELECTED}) > & {
    border-start-end-radius: ${TABLE_BORDER_RADIUS};
  }

  ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_SELECTED}:not(.${TABLE_CLASSNAMES.IS_BOTTOM_ADJACENT_SELECTED}):not(.${TABLE_CLASSNAMES.IS_LEFT_ADJACENT_SELECTED}) > & {
    border-end-start-radius: ${TABLE_BORDER_RADIUS};
  }

  ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_SELECTED}:not(.${TABLE_CLASSNAMES.IS_BOTTOM_ADJACENT_SELECTED}):not(.${TABLE_CLASSNAMES.IS_RIGHT_ADJACENT_SELECTED}) > & {
    border-end-end-radius: ${TABLE_BORDER_RADIUS};
  }

  ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_SELECTED}.${TABLE_CLASSNAMES.IS_HOVER_DELETE_BUTTON} > & {
    transition: border-color 0.2s;
    border-color: ${token('color.border.danger', colors.R500)};
  }

  /* Remove border radius when parent table row has selected class - only when visual refresh M1 is enabled */
  ${(props) =>
    props.isVisualRefreshM1Enabled &&
    `
    tr.${TABLE_CLASSNAMES.IS_SELECTED}
      ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_SELECTED}:not(.${TABLE_CLASSNAMES.IS_TOP_ADJACENT_SELECTED}):not(
        .${TABLE_CLASSNAMES.IS_LEFT_ADJACENT_SELECTED}
      )
      > & {
      border-start-start-radius: 0;
    }

    tr.${TABLE_CLASSNAMES.IS_SELECTED}
      ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_SELECTED}:not(.${TABLE_CLASSNAMES.IS_BOTTOM_ADJACENT_SELECTED}):not(
        .${TABLE_CLASSNAMES.IS_LEFT_ADJACENT_SELECTED}
      )
      > & {
      border-end-start-radius: 0;
    }

    tr.${TABLE_CLASSNAMES.IS_SELECTED}
      ${InlineEditWrapperContainer}.${TABLE_CLASSNAMES.IS_SELECTED}:not(
        .${TABLE_CLASSNAMES.IS_LEFT_ADJACENT_SELECTED})
      > & {
      border-left: none;
    }
  `}
`;

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const ActionContainer = styled.div`
  position: absolute;
  top: calc(100% + 1px);
  right: 0;
  transition: opacity 0.2s;
  z-index: 1;
  opacity: 0;
  pointer-events: none;
  background: ${token('elevation.surface', '#FFFFFF')};
  border-radius: ${token('radius.small', '3px')};

  ${InlineEditWrapperContainer}:hover > &, ${InlineEditWrapperContainer}:focus-within > & {
    opacity: 1;
    pointer-events: auto;
  }
`;

export interface InlineEditWrapperProps {
  actions?: Array<{
    id: string;
    icon: ReactChild;
    onClick: (e: React.MouseEvent) => void;
    tooltip?: string;
  }>;
  children: ReactNode;
  markAsDeleted?: boolean;
  isInvalid?: boolean;
  isSelected?: boolean;
  selectedAdjacentCells?: Adjacents;
  isLastInColumn?: boolean;
  hideBorderOnFocus?: boolean;
  className?: string;
  isDraggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDrag?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  style?: CSSProperties;
}

const InlineEditWrapper = forwardRef<HTMLDivElement, InlineEditWrapperProps>(
  function InlineEditWrapper(
    {
      actions = [],
      children,
      isInvalid,
      markAsDeleted,
      isSelected,
      selectedAdjacentCells,
      isLastInColumn,
      hideBorderOnFocus,
      className,
      isDraggable,
      onDragStart,
      onDrag,
      onDragEnd,
      style,
    },
    ref
  ) {
    const isSkeletonEnabled = useSkeletonEnabled();
    const intl = useIntl();
    const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
      'databases-frontend-visual-refresh-table-m1'
    );
    const { isStreaming } = useDatabaseStreamState();
    const isDbAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
    const isStreamingEnabled = isDbAiFirstCreationEnabled && isStreaming;
    const { isSingleFieldStreaming, currentEntryOperation, currentFieldOperation } =
      useSnapshot(aiFirstCreationStreamingState);
    return (
      <InlineEditWrapperContainer
        data-testid={`inline-edit-wrapper${markAsDeleted ? '-mark-as-deleted' : ''}`}
        data-interactive
        className={classNames([
          className,
          {
            [TABLE_CLASSNAMES.HIDE_BORDER_ON_FOCUS]: hideBorderOnFocus,
            [TABLE_CLASSNAMES.IS_INVALID]: isInvalid,
            [TABLE_CLASSNAMES.IS_SELECTED]:
              isSelected &&
              (!isStreamingEnabled ||
                currentEntryOperation === 'delete' ||
                currentFieldOperation === 'delete'),
            [TABLE_CLASSNAMES.IS_ROVO_EDIT_PROCESSING]:
              isSelected && isStreamingEnabled && !!isSingleFieldStreaming,
            [TABLE_CLASSNAMES.IS_ROVO_GRADIENT_SELECTED]:
              isStreamingEnabled && isSingleFieldStreaming,
            [TABLE_CLASSNAMES.IS_SKELETON_ENABLED]: isSkeletonEnabled,
            [TABLE_CLASSNAMES.IS_LEFT_ADJACENT_SELECTED]:
              isSelected && selectedAdjacentCells?.left,
            [TABLE_CLASSNAMES.IS_RIGHT_ADJACENT_SELECTED]:
              isSelected && selectedAdjacentCells?.right,
            [TABLE_CLASSNAMES.IS_TOP_ADJACENT_SELECTED]:
              isSelected && selectedAdjacentCells?.top,
            [TABLE_CLASSNAMES.IS_BOTTOM_ADJACENT_SELECTED]:
              isSelected && !isLastInColumn && selectedAdjacentCells?.bottom,
            [TABLE_CLASSNAMES.IS_HOVER_DELETE_BUTTON]: markAsDeleted,
          },
        ])}
        ref={ref}
        draggable={isDraggable}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
        style={style}
        intl={intl}
      >
        {children}
        {!isSkeletonEnabled && (
          <BorderContainer
            isVisualRefreshM1Enabled={isVisualRefreshM1Enabled}
            borderStyle={
              isVisualRefreshM1Enabled ? visualRefreshSelectBorder : selectedBorder
            }
          />
        )}
        {actions.length > 0 && (
          <ActionContainer>
            {actions.map((action) => (
              <Tooltip key={action.id} content={action.tooltip}>
                <Button iconBefore={action.icon} onClick={action.onClick} tabIndex={-1} />
              </Tooltip>
            ))}
          </ActionContainer>
        )}
      </InlineEditWrapperContainer>
    );
  }
);

export default InlineEditWrapper;
